/**
 * Pages administré — Location de matériel (tranche 2).
 *
 * Exporte :
 *   - LouerMaterielPage : parcourir le catalogue → réserver (dates + quantité)
 *                         → confirmation avec n° LOC.
 *   - MesLocationsPage  : suivi de ses réservations + annulation.
 *
 * Côté citoyen du module "Location de matériel". Accent tech (Service technique).
 * R13 : aucun window.alert/confirm — useConfirmDialog.
 */

import { useEffect, useState, useMemo, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import {
  Card, Button, Badge, Alert, LogoMark, SectionEyebrow,
} from '@/components/ui';
import { AppHeader } from '@/components/AppHeader';
import {
  Landmark, ArrowLeft, Boxes, PartyPopper, Armchair, ShieldAlert, Package, DoorOpen,
  Euro, CalendarDays, CheckCircle2, ChevronRight, ChevronLeft, Inbox, AlertCircle,
  FileDown, CalendarPlus, Clock, Wrench, ClipboardList,
} from 'lucide-react';
import type {
  MaterielCatalogue, MaterielCategorie,
  MaterielReservation, MaterielReservationStatut,
} from '@roronoa-collectivites/shared-types';

// ============================================================================
// Constantes partagées
// ============================================================================

const CATEGORIE_LABEL: Record<MaterielCategorie, string> = {
  evenement: 'Événement', mobilier: 'Mobilier', securite: 'Sécurité',
  outils: 'Outils', salle: 'Salle', autre: 'Autre',
};
const CATEGORIE_ICON: Record<MaterielCategorie, typeof Package> = {
  evenement: PartyPopper, mobilier: Armchair, securite: ShieldAlert,
  outils: Wrench, salle: DoorOpen, autre: Package,
};
const CATEGORIES: MaterielCategorie[] = ['mobilier', 'securite', 'outils', 'salle', 'autre'];

export const RESERVATION_STATUT: Record<MaterielReservationStatut, {
  label: string; variant: 'neutral' | 'warning' | 'success' | 'urgent' | 'info';
}> = {
  demande:   { label: 'En attente',     variant: 'warning' },
  validee:   { label: 'Validée',        variant: 'info' },
  refusee:   { label: 'Refusée',        variant: 'urgent' },
  remise:    { label: 'Matériel remis', variant: 'info' },
  retournee: { label: 'Terminée',       variant: 'success' },
  annulee:   { label: 'Annulée',        variant: 'neutral' },
};

const TOPBAR_BG = {
  backgroundImage:
    'radial-gradient(220px 80px at 10% 50%, oklch(var(--c-tech) / 0.22), transparent 70%), ' +
    'linear-gradient(90deg, oklch(var(--c-bg-2) / 0.65), oklch(var(--c-bg) / 0.45))',
};
const INPUT_CLS = 'h-10 w-full rounded-r-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';

function formatEuro(n: number): string {
  return n.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}
function formatDateFr(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
}
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}
function joursEntre(d1: string, d2: string): number {
  return Math.round(
    (new Date(d2 + 'T00:00:00').getTime() - new Date(d1 + 'T00:00:00').getTime()) / 86400000
  );
}
function addDaysIso(iso: string, days: number): string {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Télécharge le récap PDF d'une réservation (fetch authentifié → blob). */
async function triggerPdfDownload(r: MaterielReservation): Promise<void> {
  const blob = await api.materiel.reservations.pdfBlob(r.id);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `reservation-${r.numero}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Télécharge l'état des lieux d'entrée (version publique) du bien loué. */
async function triggerEtatEntreeDownload(r: MaterielReservation): Promise<void> {
  const blob = await api.materiel.reservations.etatEntreePdfBlob(r.id);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `etat-des-lieux-entree-${r.numero}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function AdmLayout({
  title, eyebrow, children, headerRight,
}: {
  title: string;
  eyebrow: string;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
}) {
  const navigate = useNavigate();
  const { tenant } = useAuth();
  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Matériel" accent="tech" dashboardTo="/dashboard" actions={headerRight} />
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-8 sm:px-6 sm:py-10 animate-fade-in">
        <div className="mb-6 flex items-center gap-3">
          <LogoMark icon={Boxes} accent="tech" size="lg" />
          <div>
            <SectionEyebrow as="div">{eyebrow}</SectionEyebrow>
            <h1 className="mt-0.5 font-display text-xl font-semibold tracking-r-snug text-ink sm:text-2xl">
              {title}
            </h1>
          </div>
        </div>
        {children}
      </main>
    </div>
  );
}

// ============================================================================
// LOUER DU MATÉRIEL — catalogue → formulaire → confirmation
// ============================================================================

export function LouerMaterielPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<MaterielCatalogue[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterCat, setFilterCat] = useState<MaterielCategorie | 'tout'>('tout');
  const [selected, setSelected] = useState<MaterielCatalogue | null>(null);
  const [confirmed, setConfirmed] = useState<MaterielReservation | null>(null);

  useEffect(() => {
    void (async () => {
      try { setItems(await api.materiel.list()); }
      catch (err) { setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement'); }
      finally { setLoading(false); }
    })();
  }, []);

  // --- Écran 3 : confirmation ---
  if (confirmed) {
    return (
      <AdmLayout title="Réservation enregistrée" eyebrow="Location de matériel">
        <Card accent="tech" className="p-6 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-success" aria-hidden="true" />
          <h2 className="mt-3 font-display text-lg font-semibold text-ink">
            Votre demande est enregistrée
          </h2>
          <p className="mt-1 text-sm text-ink-muted">
            La mairie va l'examiner. Vous serez informé de sa validation.
          </p>
          <div className="mx-auto mt-5 max-w-sm space-y-2 rounded-r-lg border border-border bg-surface/50 p-4 text-left text-sm">
            <Row label="N° de réservation" value={confirmed.numero} strong />
            {confirmed.beneficiaireType !== 'perso' && confirmed.beneficiaireNom && (
              <Row label={confirmed.beneficiaireType === 'association' ? 'Association' : 'Enseigne'} value={confirmed.beneficiaireNom} />
            )}
            {confirmed.creneau != null ? (
              <>
                <Row label="Salle" value={confirmed.materielNom} />
                <Row label="Créneau" value={confirmed.creneau === 'journee' ? 'Journée complète' : confirmed.creneau === 'matin' ? 'Demi-journée matin' : 'Demi-journée après-midi'} />
                <Row label="Date" value={formatDateFr(confirmed.dateRetrait)} />
                {confirmed.montantEuros != null && <Row label="Coût" value={`${formatEuro(confirmed.montantEuros)} €`} />}
                <Row label="Clés" value={`À récupérer la veille, ${confirmed.salleClesRetraitDebut || '16:00'}–${confirmed.salleClesRetraitFin || '17:00'}`} />
                <Row label="Caution" value={`${formatEuro(confirmed.cautionEuros)} € — à régler au retrait`} />
              </>
            ) : (
              <>
                <Row label="Matériel" value={`${confirmed.quantite} × ${confirmed.materielNom}`} />
                <Row label="Retrait" value={formatDateFr(confirmed.dateRetrait)} />
                <Row label="Retour" value={formatDateFr(confirmed.dateRetour)} />
                <Row label="Caution" value={`${formatEuro(confirmed.cautionEuros)} € — à régler au retrait`} />
              </>
            )}
          </div>

          <div className="mx-auto mt-3 max-w-sm rounded-r-lg border border-border bg-surface/30 p-3 text-left">
            <p className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold text-ink">
              <Clock className="h-3.5 w-3.5 text-tech" aria-hidden="true" /> Rappels programmés
            </p>
            <ul className="space-y-0.5 text-xs text-ink-muted">
              <li>· Mail récapitulatif — envoyé à l'instant</li>
              <li>· Rappel la veille du retrait</li>
              <li>· Rappel la veille du retour</li>
            </ul>
          </div>

          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Button variant="primary" size="sm" onClick={() => navigate('/mes-locations')}>
              Voir mes locations
            </Button>
            <Button variant="secondary" size="sm"
              onClick={() => { void triggerPdfDownload(confirmed).catch(() => {}); }}>
              <FileDown className="h-4 w-4" aria-hidden="true" /> Récap PDF
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { setConfirmed(null); setSelected(null); }}>
              Réserver autre chose
            </Button>
          </div>
        </Card>
      </AdmLayout>
    );
  }

  // --- Écran 2 : formulaire de réservation ---
  if (selected) {
    return (
      <AdmLayout title="Réserver ce matériel" eyebrow="Location de matériel">
        <ReservationForm
          materiel={selected}
          onBack={() => setSelected(null)}
          onDone={(resa) => setConfirmed(resa)}
        />
      </AdmLayout>
    );
  }

  // --- Écran 1 : catalogue ---
  const filtered = (items ?? []).filter((m) => filterCat === 'tout' || m.categorie === filterCat);

  return (
    <AdmLayout
      title="Louer du matériel / des salles"
      eyebrow="Location de matériel"
    >
      {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}

      {/* « Mes locations » a quitté le bandeau (31/07). Le header porte l'identité
          de la commune et la sortie vers le tableau de bord ; une navigation
          propre au module n'y a pas sa place. Ici, à côté des filtres, elle est
          là où l'œil la cherche. */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <CatPill active={filterCat === 'tout'} onClick={() => setFilterCat('tout')}>Tout</CatPill>
        {CATEGORIES.map((c) => (
          <CatPill key={c} active={filterCat === c} onClick={() => setFilterCat(c)}>
            {CATEGORIE_LABEL[c]}
          </CatPill>
        ))}
        <Button variant="ghost" size="sm" className="ml-auto"
          onClick={() => navigate('/mes-locations')}>
          <Inbox className="h-4 w-4" aria-hidden="true" />
          Mes locations
        </Button>
      </div>

      {loading ? (
        <p className="py-12 text-center text-sm text-ink-subtle">Chargement…</p>
      ) : filtered.length === 0 ? (
        <Card className="py-12 text-center">
          <Boxes className="mx-auto h-10 w-10 text-ink-dim" aria-hidden="true" />
          <p className="mt-3 text-sm text-ink-muted">
            Aucun matériel disponible à la location pour le moment.
          </p>
        </Card>
      ) : (
        <div className="space-y-2.5">
          {filtered.map((m) => {
            const Icon = CATEGORIE_ICON[m.categorie];
            return (
              <Card
                key={m.id}
                className="flex items-center gap-3 p-3.5 cursor-pointer transition-all hover:border-border-strong hover:shadow-soft"
                onClick={() => setSelected(m)}
              >
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-r-lg border border-tech/25 bg-tech-tint/60 text-tech-fg">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="truncate font-display text-sm font-semibold text-ink">{m.nom}</h3>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-ink-muted">
                    <span>Caution {formatEuro(m.cautionEuros)} €</span>
                    <span className={m.tarifEuros === 0 ? 'text-success' : ''}>
                      {m.tarifEuros === 0 ? 'gratuit' : `${formatEuro(m.tarifEuros)} €`}
                    </span>
                    <span>{m.quantiteTotale} {m.uniteLibelle}{m.quantiteTotale > 1 ? 's' : ''}</span>
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-ink-subtle" aria-hidden="true" />
              </Card>
            );
          })}
        </div>
      )}
    </AdmLayout>
  );
}

// ── Mini-planning de disponibilité d'une salle ───────────────────────────────
// Construction des dates en LOCAL (getFullYear/Month/Date) — JAMAIS toISOString —
// pour éviter tout décalage de jour lié au fuseau (bug déjà rencontré).
function ymdLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const MOIS_FR = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const JOURS_FR = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

function SalleDisponibiliteCalendar({
  salleId, selectedDate, selectedCreneau, onPick,
}: {
  salleId: string;
  selectedDate: string;
  selectedCreneau: 'matin' | 'apres_midi' | 'journee';
  onPick: (date: string, creneau: 'matin' | 'apres_midi' | 'journee') => void;
}) {
  const today = new Date();
  const [mois, setMois] = useState<Date>(new Date(today.getFullYear(), today.getMonth(), 1));
  const [occupes, setOccupes] = useState<Array<{ date: string; creneau: string }>>([]);
  const [loading, setLoading] = useState(false);

  const from = ymdLocal(new Date(mois.getFullYear(), mois.getMonth(), 1));
  const to = ymdLocal(new Date(mois.getFullYear(), mois.getMonth() + 1, 0));

  useEffect(() => {
    let actif = true;
    setLoading(true);
    api.materiel.reservations.disponibiliteSalle(salleId, from, to)
      .then((r) => { if (actif) setOccupes(r.occupes); })
      .catch(() => { if (actif) setOccupes([]); })
      .finally(() => { if (actif) setLoading(false); });
    return () => { actif = false; };
  }, [salleId, from, to]);

  // Pour chaque date : matin/aprem pris ?
  const prisParJour = useMemo(() => {
    const map = new Map<string, { matin: boolean; aprem: boolean }>();
    for (const o of occupes) {
      const e = map.get(o.date) ?? { matin: false, aprem: false };
      if (o.creneau === 'matin' || o.creneau === 'journee') e.matin = true;
      if (o.creneau === 'apres_midi' || o.creneau === 'journee') e.aprem = true;
      map.set(o.date, e);
    }
    return map;
  }, [occupes]);

  // Grille : décalage lundi=0. getDay() renvoie 0=dimanche → on convertit.
  const premier = new Date(mois.getFullYear(), mois.getMonth(), 1);
  const decalage = (premier.getDay() + 6) % 7; // lundi=0 … dimanche=6
  const nbJours = new Date(mois.getFullYear(), mois.getMonth() + 1, 0).getDate();
  const cases: (Date | null)[] = [];
  for (let i = 0; i < decalage; i++) cases.push(null);
  for (let d = 1; d <= nbJours; d++) cases.push(new Date(mois.getFullYear(), mois.getMonth(), d));

  const todayIsoLocal = ymdLocal(today);

  return (
    <div className="rounded-r-lg border border-border bg-surface/40 p-3">
      <div className="mb-2 flex items-center justify-between">
        <button type="button" aria-label="Mois précédent"
          onClick={() => setMois(new Date(mois.getFullYear(), mois.getMonth() - 1, 1))}
          className="grid h-7 w-7 place-items-center rounded-r-md border border-border text-ink-subtle hover:text-ink">
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="text-sm font-medium text-ink">{MOIS_FR[mois.getMonth()]} {mois.getFullYear()}</span>
        <button type="button" aria-label="Mois suivant"
          onClick={() => setMois(new Date(mois.getFullYear(), mois.getMonth() + 1, 1))}
          className="grid h-7 w-7 place-items-center rounded-r-md border border-border text-ink-subtle hover:text-ink">
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
      <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[10px] text-ink-subtle">
        {JOURS_FR.map((j) => <span key={j}>{j}</span>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cases.map((d, i) => {
          if (!d) return <span key={`v${i}`} />;
          const iso = ymdLocal(d);
          const passe = iso < todayIsoLocal;
          const pris = prisParJour.get(iso) ?? { matin: false, aprem: false };
          const complet = pris.matin && pris.aprem;
          const estSel = iso === selectedDate;
          return (
            <div key={iso}
              className={`flex flex-col rounded-r-md border p-0.5 text-center ${estSel ? 'border-tech bg-tech-tint/50' : 'border-border/60'} ${passe ? 'opacity-40' : ''}`}>
              <span className={`text-[11px] ${complet ? 'text-urgent' : 'text-ink'}`}>{d.getDate()}</span>
              <div className="mt-0.5 flex gap-0.5">
                <button type="button" disabled={passe || pris.matin}
                  aria-label={`${iso} matin`}
                  onClick={() => onPick(iso, 'matin')}
                  className={`h-2.5 flex-1 rounded-sm ${pris.matin ? 'bg-urgent/60' : (estSel && selectedCreneau !== 'apres_midi') ? 'bg-tech' : 'bg-emerald-500/50 hover:bg-emerald-500'}`}
                  title={pris.matin ? 'Matin pris' : 'Matin libre'} />
                <button type="button" disabled={passe || pris.aprem}
                  aria-label={`${iso} après-midi`}
                  onClick={() => onPick(iso, 'apres_midi')}
                  className={`h-2.5 flex-1 rounded-sm ${pris.aprem ? 'bg-urgent/60' : (estSel && selectedCreneau !== 'matin') ? 'bg-tech' : 'bg-emerald-500/50 hover:bg-emerald-500'}`}
                  title={pris.aprem ? 'Après-midi pris' : 'Après-midi libre'} />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-2 flex items-center gap-3 text-[10px] text-ink-subtle">
        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-emerald-500/60" /> libre</span>
        <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-urgent/60" /> pris</span>
        <span className="ml-auto">{loading ? 'Chargement…' : 'Cliquez un créneau libre (matin/après-midi)'}</span>
      </div>
    </div>
  );
}

function ReservationForm({
  materiel, onBack, onDone,
}: {
  materiel: MaterielCatalogue;
  onBack: () => void;
  onDone: (r: MaterielReservation) => void;
}) {
  const estSalle = materiel.categorie === 'salle';
  const [quantite, setQuantite] = useState('1');
  const [dateRetrait, setDateRetrait] = useState('');
  const [dateRetour, setDateRetour] = useState('');
  const [creneau, setCreneau] = useState<'matin' | 'apres_midi' | 'journee'>('matin');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // « Pour qui ? » — chargé si l'administré est au bureau d'une asso ou gère un
  // acteur local. Valeur : 'perso' ou 'association:<id>' / 'acteur:<id>'.
  const [affiliations, setAffiliations] = useState<Array<{ type: 'association' | 'acteur'; id: string; nom: string; detail: string | null }>>([]);
  const [beneficiaire, setBeneficiaire] = useState<string>('perso');
  // 9.1 — la déclaration de manifestation. Purement déclarative : elle informe
  // la mairie, elle n'engendre aucun acte.
  const [debitBoissons, setDebitBoissons] = useState(false);
  const [manifestationNom, setManifestationNom] = useState('');
  const [manifestationLieu, setManifestationLieu] = useState('');
  const [heureDebut, setHeureDebut] = useState('');
  const [heureFin, setHeureFin] = useState('');
  const [besoinVoirie, setBesoinVoirie] = useState('');

  /**
   * Ce que la déclaration envoie. Les chaînes vides deviennent `null` : une
   * colonne vide est plus honnête qu'une chaîne vide, et le résolveur d'arrêté
   * laissera son trou visible plutôt que d'écrire un blanc dans un acte signé.
   */
  function declarationManifestation() {
    const v = (x: string) => (x.trim() ? x.trim() : null);
    return {
      debitBoissons,
      manifestationNom: v(manifestationNom),
      manifestationLocalisation: v(manifestationLieu),
      manifestationHeureDebut: debitBoissons ? v(heureDebut) : null,
      manifestationHeureFin: debitBoissons ? v(heureFin) : null,
      besoinVoiePublique: v(besoinVoirie),
    };
  }
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.materiel.reservations.mesAffiliations();
        if (!cancelled) setAffiliations(r.affiliations);
      } catch { /* pas d'affiliation → perso seulement */ }
    })();
    return () => { cancelled = true; };
  }, []);

  function decodeBeneficiaire(): { beneficiaireType: 'perso' | 'association' | 'acteur'; beneficiaireId: string | null } {
    if (beneficiaire === 'perso') return { beneficiaireType: 'perso', beneficiaireId: null };
    const [type, id] = beneficiaire.split(':') as ['association' | 'acteur', string];
    return { beneficiaireType: type, beneficiaireId: id };
  }
  // Public tarifaire du bénéficiaire choisi (pour l'aperçu de prix salle).
  const publicChoisi: 'perso' | 'asso' | 'pro' =
    beneficiaire.startsWith('association') ? 'asso' : beneficiaire.startsWith('acteur') ? 'pro' : 'perso';

  // Tarif calculé pour une salle selon le créneau ET le public (si tarifs
  // différenciés activés sur la salle).
  const montantSalle = (() => {
    if (!estSalle) return 0;
    const diff = materiel.tarifsDifferenciesActif ?? false;
    const baseDemi = materiel.tarifDemiJourneeEuros ?? 0;
    const baseJour = materiel.tarifJourneeEuros ?? 0;
    let tDemi = baseDemi, tJour = baseJour;
    if (diff && publicChoisi === 'asso') {
      tDemi = materiel.tarifDemiJourneeAssoEuros ?? baseDemi;
      tJour = materiel.tarifJourneeAssoEuros ?? baseJour;
    } else if (diff && publicChoisi === 'pro') {
      tDemi = materiel.tarifDemiJourneeProEuros ?? baseDemi;
      tJour = materiel.tarifJourneeProEuros ?? baseJour;
    }
    return creneau === 'journee' ? tJour : tDemi;
  })();
  // Veille du créneau (récupération des clés) : J-1.
  const veilleCles = dateRetrait ? addDaysIso(dateRetrait, -1) : '';

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (estSalle) {
      if (!dateRetrait) { setError('Choisissez une date.'); return; }
      if (dateRetrait < todayIso()) { setError('La date ne peut pas être dans le passé.'); return; }
      setSubmitting(true); setError(null);
      try {
        const resa = await api.materiel.reservations.create({
          materielId: materiel.id, quantite: 1, dateRetrait, dateRetour: dateRetrait, creneau,
          ...decodeBeneficiaire(),
          ...declarationManifestation(),
        });
        onDone(resa);
      } catch (err) {
        setError(err instanceof ApiException ? err.payload.error : 'Erreur lors de la réservation');
        setSubmitting(false);
      }
      return;
    }
    const q = parseInt(quantite, 10);
    if (!q || q < 1) { setError('Indiquez une quantité valide.'); return; }
    if (!dateRetrait || !dateRetour) { setError('Indiquez les dates de retrait et de retour.'); return; }
    if (dateRetour < dateRetrait) { setError('La date de retour doit suivre le retrait.'); return; }
    if (dateRetrait < todayIso()) { setError('La date de retrait ne peut pas être dans le passé.'); return; }
    if (materiel.dureeMaxJours != null && joursEntre(dateRetrait, dateRetour) > materiel.dureeMaxJours) {
      setError(`Ce matériel se prête au maximum ${materiel.dureeMaxJours} jour(s).`); return;
    }
    setSubmitting(true); setError(null);
    try {
      const resa = await api.materiel.reservations.create({
        materielId: materiel.id, quantite: q, dateRetrait, dateRetour,
        ...decodeBeneficiaire(),
      });
      onDone(resa);
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur lors de la réservation');
      setSubmitting(false);
    }
  }

  return (
    <Card className="p-5">
      <div className="mb-4 flex items-center gap-3 border-b border-border pb-4">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-r-lg border border-tech/25 bg-tech-tint/60 text-tech-fg">
          <Boxes className="h-5 w-5" aria-hidden="true" />
        </span>
        <div>
          <h2 className="font-display text-sm font-semibold text-ink">{materiel.nom}</h2>
          <p className="text-xs text-ink-subtle">
            {CATEGORIE_LABEL[materiel.categorie]} · caution {formatEuro(materiel.cautionEuros)} €
            {materiel.categorie === 'salle'
              ? ` · location à la demi-journée ou à la journée`
              : ` / ${materiel.uniteLibelle}`}
          </p>
          {materiel.categorie === 'salle' && (
            <p className="text-xs text-ink-subtle">
              {materiel.tarifDemiJourneeEuros != null && `½ journée : ${formatEuro(materiel.tarifDemiJourneeEuros)} €`}
              {materiel.tarifDemiJourneeEuros != null && materiel.tarifJourneeEuros != null && ' · '}
              {materiel.tarifJourneeEuros != null && `journée : ${formatEuro(materiel.tarifJourneeEuros)} €`}
              {(materiel.horaireMatinDebut || materiel.horaireApremDebut) && (
                <>
                  {' · '}
                  {materiel.horaireMatinDebut && `matin ${materiel.horaireMatinDebut}–${materiel.horaireMatinFin ?? ''}`}
                  {materiel.horaireMatinDebut && materiel.horaireApremDebut && ' / '}
                  {materiel.horaireApremDebut && `après-midi ${materiel.horaireApremDebut}–${materiel.horaireApremFin ?? ''}`}
                </>
              )}
            </p>
          )}
        </div>
      </div>

      {materiel.description && (
        <p className="mb-4 text-sm text-ink-muted">{materiel.description}</p>
      )}
      {error && <Alert variant="error" icon={AlertCircle} className="mb-4">{error}</Alert>}

      <form onSubmit={submit} className="space-y-4">
        {/* « Pour qui ? » — visible seulement si l'administré a des affiliations
            (bureau d'asso, acteur local géré). Détermine le bénéficiaire et le
            tarif appliqué (perso / associatif / professionnel). */}
        {affiliations.length > 0 && (
          <div>
            <label htmlFor="beneficiaire" className="mb-1 block text-xs font-medium text-ink-muted">
              Cette réservation, c'est pour&nbsp;?
            </label>
            <select id="beneficiaire" value={beneficiaire} onChange={(e) => setBeneficiaire(e.target.value)}
              className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-sm text-ink">
              <option value="perso">Moi-même (à titre personnel)</option>
              {affiliations.filter((a) => a.type === 'association').length > 0 && (
                <optgroup label="Mes associations (bureau)">
                  {affiliations.filter((a) => a.type === 'association').map((a) => (
                    <option key={`association:${a.id}`} value={`association:${a.id}`}>{a.nom}{a.detail ? ` — ${a.detail}` : ''}</option>
                  ))}
                </optgroup>
              )}
              {affiliations.filter((a) => a.type === 'acteur').length > 0 && (
                <optgroup label="Mes enseignes / acteurs locaux">
                  {affiliations.filter((a) => a.type === 'acteur').map((a) => (
                    <option key={`acteur:${a.id}`} value={`acteur:${a.id}`}>{a.nom}{a.detail ? ` — ${a.detail}` : ''}</option>
                  ))}
                </optgroup>
              )}
            </select>
            {publicChoisi !== 'perso' && (
              <p className="mt-1 text-xs text-ink-subtle">
                Réservation au nom de {publicChoisi === 'asso' ? 'l\'association' : 'votre enseigne'} — le tarif applicable ({publicChoisi === 'asso' ? 'associatif' : 'professionnel'}) sera confirmé après validation.
              </p>
            )}
          </div>
        )}

        {/* ── 9.1 — ce que l'organisateur annonce avec sa demande ─────────────
            Déclaratif, et rien d'autre : cocher « buvette » ne fabrique aucun
            arrêté. La mairie le voit sur la demande et décide. Réservé aux
            salles : une tondeuse n'ouvre pas de débit de boissons. */}
        {estSalle && (
          <div className="rounded-r-lg border border-border/60 p-3">
            <p className="text-xs font-medium text-ink">Votre manifestation</p>
            <p className="mt-0.5 text-xs text-ink-subtle">
              Ces informations permettent à la mairie de préparer les autorisations
              nécessaires. Laissez vide si votre réservation ne concerne pas un
              événement ouvert au public.
            </p>

            <div className="mt-3 space-y-3">
              <div>
                <label htmlFor="manif-nom" className="mb-1 block text-xs font-medium text-ink-muted">
                  Nom de la manifestation
                </label>
                <input id="manif-nom" type="text" maxLength={200}
                  value={manifestationNom} onChange={(e) => setManifestationNom(e.target.value)}
                  placeholder="Ex : Loto du Téléthon"
                  className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-sm text-ink" />
              </div>

              <label className="flex items-start gap-2 text-sm text-ink">
                <input type="checkbox" className="mt-0.5 h-4 w-4 rounded border-border"
                  checked={debitBoissons} onChange={(e) => setDebitBoissons(e.target.checked)} />
                <span>
                  Nous tiendrons une buvette (débit de boissons temporaire)
                  <span className="block text-xs text-ink-subtle">
                    Une autorisation du maire est nécessaire. Elle est limitée à
                    quelques manifestations par an et par association.
                  </span>
                </span>
              </label>

              {/* Les horaires et le lieu ne servent qu'à la buvette : on ne les
                  demande qu'une fois la case cochée, pour ne pas alourdir un
                  formulaire que la plupart des gens remplissent sans buvette. */}
              {debitBoissons && (
                <div className="space-y-3 border-l-2 border-border pl-3">
                  <div>
                    <label htmlFor="manif-lieu" className="mb-1 block text-xs font-medium text-ink-muted">
                      Où se tient la buvette
                    </label>
                    <input id="manif-lieu" type="text" maxLength={300}
                      value={manifestationLieu} onChange={(e) => setManifestationLieu(e.target.value)}
                      placeholder="Ex : sous le préau, devant la salle"
                      className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-sm text-ink" />
                  </div>
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <label htmlFor="manif-h1" className="mb-1 block text-xs font-medium text-ink-muted">
                        Ouverture
                      </label>
                      <input id="manif-h1" type="time" value={heureDebut}
                        onChange={(e) => setHeureDebut(e.target.value)}
                        className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-sm text-ink" />
                    </div>
                    <div className="flex-1">
                      <label htmlFor="manif-h2" className="mb-1 block text-xs font-medium text-ink-muted">
                        Fermeture
                      </label>
                      <input id="manif-h2" type="time" value={heureFin}
                        onChange={(e) => setHeureFin(e.target.value)}
                        className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3 text-sm text-ink" />
                    </div>
                  </div>
                  <p className="text-xs text-ink-subtle">
                    Les heures réelles d'ouverture au public, qui peuvent différer
                    du créneau de salle.
                  </p>
                </div>
              )}

              <div>
                <label htmlFor="manif-voirie" className="mb-1 block text-xs font-medium text-ink-muted">
                  Besoin sur la voie publique (facultatif)
                </label>
                <textarea id="manif-voirie" rows={2} maxLength={2000}
                  value={besoinVoirie} onChange={(e) => setBesoinVoirie(e.target.value)}
                  placeholder="Ex : fermer la contre-allée samedi de 14 h à minuit"
                  className="w-full rounded-r-lg border border-border bg-surface/60 px-3 py-2 text-sm text-ink" />
                <p className="mt-1 text-xs text-ink-subtle">
                  Décrivez simplement ce dont vous avez besoin. La mairie appréciera
                  si un arrêté est nécessaire et lequel.
                </p>
              </div>
            </div>
          </div>
        )}

        {estSalle ? (
          <>
            <SalleDisponibiliteCalendar
              salleId={materiel.id}
              selectedDate={dateRetrait}
              selectedCreneau={creneau}
              onPick={(date, cr) => { setDateRetrait(date); setCreneau(cr); }}
            />
            <div>
              <span className="mb-1 block text-xs font-medium text-ink-muted">Créneau</span>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {([
                  ['matin', 'Demi-journée matin', materiel.horaireMatinDebut && materiel.horaireMatinFin ? `${materiel.horaireMatinDebut}–${materiel.horaireMatinFin}` : null, materiel.tarifDemiJourneeEuros],
                  ['apres_midi', 'Demi-journée après-midi', materiel.horaireApremDebut && materiel.horaireApremFin ? `${materiel.horaireApremDebut}–${materiel.horaireApremFin}` : null, materiel.tarifDemiJourneeEuros],
                  ['journee', 'Journée complète', 'créneau de la veille au lendemain', materiel.tarifJourneeEuros],
                ] as const).map(([val, label, hrs, tarif]) => (
                  <button
                    key={val} type="button" onClick={() => setCreneau(val)}
                    className={`rounded-r-lg border p-3 text-left transition ${creneau === val ? 'border-tech bg-tech-tint/60 text-ink' : 'border-border bg-surface/40 text-ink-muted hover:border-tech/40'}`}
                  >
                    <span className="block text-sm font-medium text-ink">{label}</span>
                    {hrs && <span className="block text-[11px] text-ink-subtle">{hrs}</span>}
                    {tarif != null && <span className="mt-1 block text-xs font-semibold text-tech-fg">{formatEuro(tarif)} €</span>}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label htmlFor="rf-date" className="mb-1 block text-xs font-medium text-ink-muted">
                Date de la réservation
              </label>
              <input
                id="rf-date" type="date" min={todayIso()}
                value={dateRetrait}
                onChange={(e) => setDateRetrait(e.target.value)}
                className={INPUT_CLS}
              />
            </div>
            <div className="rounded-r-lg border border-border bg-surface/40 p-3 text-xs text-ink-muted">
              <Clock className="mr-1 inline h-3.5 w-3.5 text-tech" aria-hidden="true" />
              Remise des clés : à récupérer la veille
              {veilleCles && <span className="font-medium text-ink"> ({formatDateFr(veilleCles)})</span>}
              {' '}entre {materiel.clesRetraitDebut || '16:00'} et {materiel.clesRetraitFin || '17:00'},
              {creneau === 'journee'
                ? ' occupation du créneau de la veille au lendemain.'
                : ' restitution possible le lendemain matin.'}
            </div>
            <div className="rounded-r-lg border border-border bg-surface/40 p-3 text-xs text-ink-muted">
              <Euro className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
              Coût de la location : <span className="font-semibold text-ink">{formatEuro(montantSalle)} €</span>
              {' · '}Caution : <span className="font-semibold text-ink">{formatEuro(materiel.cautionEuros)} €</span> — à régler au retrait.
            </div>
          </>
        ) : (
          <>
            <div>
              <label htmlFor="rf-qte" className="mb-1 block text-xs font-medium text-ink-muted">
                Quantité ({materiel.uniteLibelle})
              </label>
              <input
                id="rf-qte" type="number" min={1} max={materiel.quantiteTotale}
                value={quantite}
                onChange={(e) => setQuantite(e.target.value)}
                className={INPUT_CLS}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="rf-retrait" className="mb-1 block text-xs font-medium text-ink-muted">
                  Date de retrait
                </label>
                <input
                  id="rf-retrait" type="date" min={todayIso()}
                  value={dateRetrait}
                  onChange={(e) => setDateRetrait(e.target.value)}
                  className={INPUT_CLS}
                />
              </div>
              <div>
                <label htmlFor="rf-retour" className="mb-1 block text-xs font-medium text-ink-muted">
                  Date de retour
                </label>
                <input
                  id="rf-retour" type="date"
                  min={dateRetrait || todayIso()}
                  max={
                    materiel.dureeMaxJours != null && dateRetrait
                      ? addDaysIso(dateRetrait, materiel.dureeMaxJours)
                      : undefined
                  }
                  value={dateRetour}
                  onChange={(e) => setDateRetour(e.target.value)}
                  className={INPUT_CLS}
                />
              </div>
            </div>

            {materiel.dureeMaxJours != null && (
              <p className="flex items-center gap-1.5 text-xs text-ink-muted">
                <Clock className="h-3.5 w-3.5 text-tech" aria-hidden="true" />
                Durée de prêt maximale : {materiel.dureeMaxJours} jour{materiel.dureeMaxJours > 1 ? 's' : ''}.
              </p>
            )}

            <div className="rounded-r-lg border border-border bg-surface/40 p-3 text-xs text-ink-muted">
              <Euro className="mr-1 inline h-3.5 w-3.5" aria-hidden="true" />
              Caution totale à prévoir : <span className="font-semibold text-ink">
                {formatEuro(materiel.cautionEuros * (parseInt(quantite, 10) || 0))} €
              </span> — à régler au retrait (chèque ou virement).
            </div>
          </>
        )}

        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-4">
          <Button type="submit" variant="primary" size="sm" disabled={submitting}>
            <CalendarDays className="h-4 w-4" aria-hidden="true" />
            {submitting ? 'Envoi…' : 'Envoyer la demande'}
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={onBack}>
            Retour au catalogue
          </Button>
        </div>
      </form>
    </Card>
  );
}

// ============================================================================
// MES LOCATIONS
// ============================================================================

export function MesLocationsPage() {
  const navigate = useNavigate();
  const { confirm, dialogElement } = useConfirmDialog();
  const [items, setItems] = useState<MaterielReservation[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [prolongId, setProlongId] = useState<string | null>(null);
  const [prolongDate, setProlongDate] = useState('');

  async function reload() {
    setLoading(true); setError(null);
    try { setItems(await api.materiel.reservations.list()); }
    catch (err) { setError(err instanceof ApiException ? err.payload.error : 'Erreur de chargement'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void reload(); }, []);

  async function pdf(r: MaterielReservation) {
    try { await triggerPdfDownload(r); }
    catch (err) { setError(err instanceof ApiException ? err.payload.error : 'Téléchargement du PDF impossible'); }
  }

  async function etatEntree(r: MaterielReservation) {
    try { await triggerEtatEntreeDownload(r); }
    catch (err) { setError(err instanceof ApiException ? err.payload.error : 'État des lieux d\u2019entrée indisponible pour ce bien'); }
  }

  async function prolonger(r: MaterielReservation) {
    if (!prolongDate) { setError('Choisissez une nouvelle date de retour.'); return; }
    if (prolongDate <= r.dateRetour) {
      setError('La nouvelle date doit être postérieure à la date de retour actuelle.'); return;
    }
    setBusyId(r.id); setError(null);
    try {
      await api.materiel.reservations.prolonger(r.id, { dateRetour: prolongDate });
      setProlongId(null); setProlongDate('');
      await reload();
    } catch (err) {
      setError(err instanceof ApiException ? err.payload.error : 'Erreur lors de la prolongation');
    } finally { setBusyId(null); }
  }

  async function annuler(r: MaterielReservation) {
    const ok = await confirm({
      title: 'Annuler cette réservation ?',
      description: `${r.numero} — ${r.materielNom}. Cette action est définitive.`,
      confirmLabel: 'Annuler la réservation',
      danger: true,
    });
    if (!ok) return;
    try { await api.materiel.reservations.annuler(r.id); await reload(); }
    catch (err) { setError(err instanceof ApiException ? err.payload.error : 'Erreur'); }
  }

  return (
    <AdmLayout
      title="Mes locations"
      eyebrow="Location de matériel"
      headerRight={
        <Button variant="ghost" size="sm" onClick={() => navigate('/louer-materiel')}>
          <Boxes className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Louer</span>
        </Button>
      }
    >
      {dialogElement}
      {error && <Alert variant="error" icon={AlertCircle} className="mb-3">{error}</Alert>}

      {loading ? (
        <p className="py-12 text-center text-sm text-ink-subtle">Chargement…</p>
      ) : !items || items.length === 0 ? (
        <Card className="py-12 text-center">
          <Inbox className="mx-auto h-10 w-10 text-ink-dim" aria-hidden="true" />
          <p className="mt-3 text-sm text-ink-muted">Vous n'avez aucune réservation.</p>
          <Button variant="primary" size="sm" className="mt-4" onClick={() => navigate('/louer-materiel')}>
            <Boxes className="h-4 w-4" aria-hidden="true" />
            Louer du matériel / des salles
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((r) => {
            const st = RESERVATION_STATUT[r.statut];
            const annulable = r.statut === 'demande' || r.statut === 'validee';
            const prolongeable = r.creneau == null && (r.statut === 'validee' || r.statut === 'remise') && r.prolongationCount === 0;
            return (
              <Card key={r.id} className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-display text-sm font-semibold text-ink">{r.materielNom}</h3>
                      <Badge variant={st.variant}>{st.label}</Badge>
                    </div>
                    <p className="mt-0.5 text-xs text-ink-subtle">{r.numero}</p>
                  </div>
                  <span className="text-xs text-ink-muted">
                    {r.quantite} unité{r.quantite > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-border pt-3 text-xs text-ink-muted">
                  <span className="flex items-center gap-1">
                    <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                    {formatDateFr(r.dateRetrait)} → {formatDateFr(r.dateRetour)}
                  </span>
                  <span>Caution {formatEuro(r.cautionEuros)} €</span>
                  {/* RETIRÉ le 31/07 — « Générer un arrêté » n'a rien à faire ici.
                      Cet écran est « Mes locations », côté administré : rédiger un
                      arrêté est un acte de police réservé à la mairie, et le lien
                      renvoyait de toute façon au tableau de bord faute de droit.
                      Le bouton reste sur l'écran Réservations du service technique
                      et de la GRC, qui sont les seuls à pouvoir s'en servir. */}
                </div>
                {r.statut === 'refusee' && r.motifRefus && (
                  <p className="mt-2 rounded-r-lg bg-urgent-tint/50 p-2 text-xs text-urgent">
                    Motif du refus : {r.motifRefus}
                  </p>
                )}
                {r.prolongationCount > 0 && (
                  <p className="mt-2 text-xs text-ink-subtle">
                    Réservation prolongée une fois{r.dateRetourInitiale
                      ? ` (retour initial : ${formatDateFr(r.dateRetourInitiale)})` : ''}.
                  </p>
                )}

                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
                  <Button variant="secondary" size="sm" onClick={() => void pdf(r)}>
                    <FileDown className="h-4 w-4" aria-hidden="true" /> Récap PDF
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => void etatEntree(r)}>
                    <ClipboardList className="h-4 w-4" aria-hidden="true" /> État des lieux d'entrée
                  </Button>
                  {prolongeable && (
                    <Button
                      variant="ghost" size="sm" disabled={busyId === r.id}
                      onClick={() => {
                        setProlongId(prolongId === r.id ? null : r.id);
                        setProlongDate(''); setError(null);
                      }}
                    >
                      <CalendarPlus className="h-4 w-4" aria-hidden="true" /> Prolonger
                    </Button>
                  )}
                  {annulable && (
                    <Button
                      variant="ghost" size="sm" className="text-urgent-fg"
                      disabled={busyId === r.id} onClick={() => annuler(r)}
                    >
                      Annuler
                    </Button>
                  )}
                </div>

                {prolongId === r.id && (
                  <div className="mt-2 flex flex-wrap items-end gap-2 rounded-r-lg border border-border bg-surface/40 p-3">
                    <div>
                      <label htmlFor={`pr-${r.id}`} className="mb-1 block text-xs font-medium text-ink-muted">
                        Nouvelle date de retour
                      </label>
                      <input
                        id={`pr-${r.id}`} type="date" min={r.dateRetour}
                        value={prolongDate}
                        onChange={(e) => setProlongDate(e.target.value)}
                        className={INPUT_CLS + ' w-44'}
                      />
                    </div>
                    <Button variant="primary" size="sm" disabled={busyId === r.id}
                      onClick={() => void prolonger(r)}>
                      Confirmer la prolongation
                    </Button>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </AdmLayout>
  );
}

// ============================================================================
// Sous-composants
// ============================================================================

function CatPill({
  children, active, onClick,
}: { children: React.ReactNode; active?: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'inline-flex h-8 items-center rounded-r-pill border px-3 text-xs font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg ' +
        (active
          ? 'border-tech/30 bg-tech-tint/80 text-tech-fg'
          : 'border-border bg-surface/40 text-ink-muted hover:text-ink hover:border-border-strong')
      }
    >
      {children}
    </button>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs text-ink-subtle">{label}</span>
      <span className={'text-right ' + (strong ? 'font-display text-sm font-semibold text-tech-fg' : 'text-ink')}>
        {value}
      </span>
    </div>
  );
}
